/**
 * Iter 31.294 + 31.295 — Multi-Agent Tokens panel.
 *
 * One tile per operator that lists every active per-agent MCP token
 * (Claude / Gemini / Copilot) and lets Number 6 mint fresh ones with
 * a coarse-bucket capability picker. Capabilities inherit from the
 * agent's last-minted token by default — matching Six's AuDHD
 * "just work" floor: repeat mints keep the abilities you last used
 * without having to re-tick every checkbox.
 *
 * Canon Rule 2 handling: the raw token is only rendered inside the
 * "reveal secret" modal on the very response that minted it. The
 * list endpoint returns previews only; the reveal modal auto-clears
 * after Six clicks Dismiss. There is no "show me again" path — the
 * only recovery is re-mint (which explicitly rotates the abilities).
 */
import React, { useCallback, useEffect, useReducer } from "react";
import { api } from "@/lib/api";
import {
    Loader2, KeyRound, AlertTriangle, Copy, CheckCircle2,
    Plus, ShieldCheck, Trash2,
} from "lucide-react";

const AGENT_LABELS = ["claude", "gemini", "copilot", "chatgpt"];

const INITIAL = {
    tokens: [],
    buckets: [],          // [{key, tools, count}]
    agents: [],           // Iter 31.303 — from /admin/agents (data-driven)
    loading: true,
    err: null,
    // mint form
    mintAgent: "gemini",
    mintCaps: null,       // null → inherit from last; ["*"] → all; [..] → picks
    mintBusy: false,
    mintErr: null,
    // reveal + copy
    revealed: null,       // { id, secret, connectorUrl, agent_label, capabilities }
    copiedToken: false,
    copiedUrl: false,
    // revoke
    revokingId: null,
};

function reducer(state, action) {
    switch (action.type) {
        case "load_ok":
            return {
                ...state,
                tokens: action.tokens,
                buckets: action.buckets,
                agents: action.agents,
                loading: false,
                err: null,
                // Iter 31.303 — if the current mintAgent isn't in the
                // fresh registry list, default to the first active one.
                mintAgent: (action.agents.some((a) => a.key === state.mintAgent)
                    ? state.mintAgent
                    : (action.agents[0]?.key || state.mintAgent)),
            };
        case "load_err":
            return { ...state, loading: false, err: action.err };
        case "set_mint_agent":
            return { ...state, mintAgent: action.agent, mintCaps: null, mintErr: null };
        case "toggle_bucket": {
            const current = state.mintCaps || bucketsForInherit(state);
            const key = action.key;
            let next;
            if (key === "*") {
                next = current.includes("*") ? [] : ["*"];
            } else {
                const without = current.filter((c) => c !== "*" && c !== key);
                next = current.includes(key) ? without : [...without, key];
            }
            return { ...state, mintCaps: next, mintErr: null };
        }
        case "reset_mint_caps":
            return { ...state, mintCaps: null };
        case "mint_busy":
            return { ...state, mintBusy: true, mintErr: null };
        case "mint_ok":
            return {
                ...state, mintBusy: false, revealed: action.revealed,
                copiedToken: false, copiedUrl: false,
                // Reset the picker so the next mint starts fresh from inheritance.
                mintCaps: null,
            };
        case "mint_err":
            return { ...state, mintBusy: false, mintErr: action.err };
        case "dismiss_reveal":
            return { ...state, revealed: null, copiedToken: false, copiedUrl: false };
        case "set_copied_token":
            return { ...state, copiedToken: action.v };
        case "set_copied_url":
            return { ...state, copiedUrl: action.v };
        case "revoke_busy":
            return { ...state, revokingId: action.id };
        case "revoke_done":
            return { ...state, revokingId: null };
        default:
            return state;
    }
}

// Compute the buckets the mint form should render as ticked when the
// user hasn't yet touched any checkbox (`mintCaps === null`). This
// mirrors the backend's inheritance rule: take the most recent token
// for the currently-selected agent, else default to ['*'].
function bucketsForInherit(state) {
    const last = (state.tokens || []).find(
        (t) => t.agent_label === state.mintAgent && t.active,
    ) || (state.tokens || []).find((t) => t.agent_label === state.mintAgent);
    if (last && Array.isArray(last.capabilities)) {
        return [...last.capabilities];
    }
    return ["*"];
}

function BucketCheckbox({ bucketKey, count, checked, onToggle, testid }) {
    return (
        <label
            className={`flex items-center gap-2 px-2 py-1 border ${checked ? "border-stamp-red text-stamp-red bg-stamp-red/5" : "border-ink/30 text-ink-soft hover:border-ink"} cursor-pointer transition-colors`}
            data-testid={testid}
        >
            <input
                type="checkbox"
                checked={checked}
                onChange={onToggle}
                className="w-3 h-3 accent-stamp-red"
                data-testid={`${testid}-input`}
            />
            <code className="font-mono text-[10px] flex-1">{bucketKey}</code>
            {count !== undefined ? (
                <span className="font-body text-[9px] text-ink-faint">
                    {count}
                </span>
            ) : null}
        </label>
    );
}

function TokenRow({ token, onRevoke, revokingId }) {
    const busy = revokingId === token.id;
    const caps = Array.isArray(token.capabilities) ? token.capabilities : null;
    const capsLabel = caps === null
        ? "legacy · all tools"
        : caps.includes("*") ? "all buckets" : caps.join(" · ") || "denied";
    return (
        <div
            className="flex items-start justify-between gap-3 py-2.5 border-b border-ink/15 last:border-b-0"
            data-testid={`mat-token-row-${token.id}`}
        >
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                    <ShieldCheck className={`w-3.5 h-3.5 ${token.active ? "text-emerald-700" : "text-ink-faint"}`} />
                    <code className="font-mono text-[11px] text-ink font-bold uppercase">
                        {token.agent_label}
                    </code>
                    <span className={`inline-block px-1.5 py-0.5 border text-[9px] font-heading uppercase tracking-[0.18em] ${token.active ? "text-emerald-700 border-emerald-700" : "text-ink-faint border-ink/30"}`}>
                        {token.active ? "active" : "revoked"}
                    </span>
                </div>
                <div className="pl-5 space-y-0.5">
                    <div className="text-[10px] text-ink-soft font-body">
                        <code className="font-mono text-stamp-red">{token.preview || "—"}</code>
                        <span className="text-ink-faint ml-2">
                            minted {token.minted_at || "—"}
                        </span>
                    </div>
                    <div className="text-[10px] text-ink-soft font-body">
                        <span className="text-ink-faint">buckets:</span>{" "}
                        <code className="font-mono">{capsLabel}</code>
                    </div>
                    {token.note ? (
                        <div className="text-[10px] text-ink-faint italic font-body">
                            {token.note}
                        </div>
                    ) : null}
                </div>
            </div>
            {token.active ? (
                <button
                    type="button"
                    onClick={() => onRevoke(token.id)}
                    disabled={busy}
                    data-testid={`mat-revoke-${token.id}`}
                    className="font-heading uppercase text-[9px] tracking-[0.22em] px-2 py-1 border border-ink/30 text-ink-soft hover:text-stamp-red hover:border-stamp-red disabled:opacity-50 inline-flex items-center gap-1 shrink-0"
                    title="Revoke this token — its bearer will 401 immediately"
                >
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                    revoke
                </button>
            ) : null}
        </div>
    );
}

export default function MultiAgentTokensPanel() {
    const [s, dispatch] = useReducer(reducer, INITIAL);

    const reload = useCallback(async () => {
        try {
            const [tokRes, bktRes, agRes] = await Promise.all([
                api.get("/admin/handler-context-tokens"),
                api.get("/admin/mcp-tool-buckets"),
                api.get("/admin/agents"),
            ]);
            const activeAgents = (agRes.data?.agents || []).filter((a) => a.active);
            dispatch({
                type: "load_ok",
                tokens: tokRes.data?.tokens || [],
                buckets: bktRes.data?.buckets || [],
                agents: activeAgents,
            });
        } catch (e) {
            dispatch({
                type: "load_err",
                err: `[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`,
            });
        }
    }, []);

    useEffect(() => { reload(); }, [reload]);

    const effectiveCaps = s.mintCaps === null ? bucketsForInherit(s) : s.mintCaps;
    const inheritedFromLast = s.mintCaps === null && (s.tokens || []).some(
        (t) => t.agent_label === s.mintAgent,
    );

    const mint = useCallback(async () => {
        const capsToSend = s.mintCaps; // null → inherit on server; otherwise explicit
        if (!window.confirm(
            `Mint a new ${s.mintAgent} token?\n\n` +
            "The full secret will be shown ONCE. Paste it into the agent's config " +
            "and dismiss immediately — do NOT paste it into a chat transcript " +
            "(canon Rule 2)."
        )) return;
        dispatch({ type: "mint_busy" });
        try {
            const { data } = await api.post("/admin/mint-handler-context-key", {
                agent_label: s.mintAgent,
                ...(capsToSend !== null ? { capabilities: capsToSend } : {}),
            });
            dispatch({
                type: "mint_ok",
                revealed: {
                    id: data.id,
                    agent_label: data.agent_label,
                    secret: data.secret,
                    connectorUrl: data.mcp_connector_url || "",
                    capabilities: data.capabilities || [],
                    inherited: !!data.capabilities_inherited,
                },
            });
            reload();
        } catch (e) {
            dispatch({
                type: "mint_err",
                err: `[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`,
            });
        }
    }, [s.mintAgent, s.mintCaps, reload]);

    const revoke = useCallback(async (id) => {
        if (!window.confirm(
            "Revoke this token? Its bearer will 401 on the next MCP call.\n\n" +
            "The receiving agent will need a fresh mint to keep working."
        )) return;
        dispatch({ type: "revoke_busy", id });
        try {
            await api.post("/admin/revoke-handler-context-token", { token_id: id });
        } catch (_) {
            // Silent — revoke is idempotent; reload will re-sync state.
        } finally {
            dispatch({ type: "revoke_done" });
            reload();
        }
    }, [reload]);

    return (
        <div
            className="paper-card border-2 border-ink/30 p-5 mb-6"
            data-testid="multi-agent-tokens-panel"
        >
            <div className="flex items-center justify-between mb-3">
                <h2 className="font-heading uppercase tracking-[0.22em] text-sm text-ink flex items-center gap-2">
                    <KeyRound className="w-4 h-4 text-stamp-red" />
                    Multi-Agent Tokens
                </h2>
                <button
                    type="button"
                    onClick={reload}
                    disabled={s.loading}
                    data-testid="mat-refresh"
                    className="font-heading uppercase tracking-[0.16em] text-[10px] text-ink-soft hover:text-stamp-red disabled:opacity-50"
                >
                    {s.loading ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "refresh"}
                </button>
            </div>

            <div className="font-body text-[11px] text-ink-soft mb-3 leading-relaxed">
                Iter 31.294 + 31.295 — per-agent MCP bearer tokens (Claude / Gemini / Copilot).
                Every ledger row filed through this token carries its <code className="font-mono">source_agent</code>{" "}
                for audit. Capabilities are coarse buckets; the mint form pre-fills with the agent&apos;s{" "}
                last-used buckets so repeat mints match the previous ability set by default.
            </div>

            {s.err ? (
                <div className="mb-3 label-typed text-[11px] leading-relaxed border-l-2 border-stamp-red pl-3 text-stamp-red flex items-start gap-1" data-testid="mat-load-err">
                    <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                    <span>{s.err}</span>
                </div>
            ) : null}

            {/* Reveal-secret block */}
            {s.revealed ? (
                <div
                    className="mb-3 border-2 border-stamp-red bg-stamp-red/5 p-3"
                    data-testid="mat-revealed"
                >
                    <div className="label-typed text-[10px] text-stamp-red mb-2 flex items-center gap-2">
                        <AlertTriangle className="w-3 h-3" />
                        New <code className="font-mono">{s.revealed.agent_label}</code> token — shown ONCE
                    </div>
                    <textarea
                        readOnly
                        value={s.revealed.secret}
                        rows={2}
                        onFocus={(e) => e.target.select()}
                        data-testid="mat-revealed-value"
                        className="w-full px-2 py-1 border border-ink/30 bg-paper-light font-mono text-[11px] text-ink select-all break-all"
                    />
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <button
                            type="button"
                            data-testid="mat-copy-token"
                            onClick={async () => {
                                try { await navigator.clipboard.writeText(s.revealed.secret); dispatch({ type: "set_copied_token", v: true }); setTimeout(() => dispatch({ type: "set_copied_token", v: false }), 2400); } catch (_) { /* clipboard */ }
                            }}
                            className="font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-1.5 border border-ink/40 text-ink hover:border-stamp-red hover:text-stamp-red inline-flex items-center gap-1.5"
                        >
                            <Copy className="w-3 h-3" />
                            {s.copiedToken ? "Copied ✓" : "Copy token"}
                        </button>
                        {s.revealed.connectorUrl ? (
                            <button
                                type="button"
                                data-testid="mat-copy-url"
                                onClick={async () => {
                                    try { await navigator.clipboard.writeText(s.revealed.connectorUrl); dispatch({ type: "set_copied_url", v: true }); setTimeout(() => dispatch({ type: "set_copied_url", v: false }), 2400); } catch (_) { /* clipboard */ }
                                }}
                                className="font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-1.5 border border-stamp-red text-stamp-red hover:bg-stamp-red/10 inline-flex items-center gap-1.5"
                            >
                                <Copy className="w-3 h-3" />
                                {s.copiedUrl ? "Copied ✓" : "Copy connector URL"}
                            </button>
                        ) : null}
                        <button
                            type="button"
                            data-testid="mat-dismiss"
                            onClick={() => dispatch({ type: "dismiss_reveal" })}
                            className="font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-1.5 border border-ink/30 text-ink-soft hover:text-stamp-red"
                        >
                            Dismiss
                        </button>
                    </div>
                    <div className="font-body text-[10px] text-ink-soft mt-2 leading-relaxed">
                        Buckets granted: <code className="font-mono text-stamp-red">
                            {(s.revealed.capabilities || []).join(" · ") || "none"}
                        </code>
                        {s.revealed.inherited ? " · inherited from previous mint" : ""}
                    </div>
                </div>
            ) : null}

            {/* Mint form */}
            <div className="border border-ink/20 p-3 mb-4" data-testid="mat-mint-form">
                <div className="label-typed text-[10px] text-ink mb-2 flex items-center gap-2">
                    <Plus className="w-3 h-3" />
                    Mint new token
                </div>
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                    {(s.agents.length > 0 ? s.agents.map((a) => a.key) : AGENT_LABELS).map((a) => (
                        <button
                            key={a}
                            type="button"
                            onClick={() => dispatch({ type: "set_mint_agent", agent: a })}
                            data-testid={`mat-agent-${a}`}
                            className={`font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-1 border ${s.mintAgent === a ? "border-stamp-red text-stamp-red bg-stamp-red/5" : "border-ink/30 text-ink-soft hover:border-ink"}`}
                        >
                            {a}
                        </button>
                    ))}
                </div>
                <div className="mb-2">
                    <div className="font-body text-[10px] text-ink-soft mb-1.5">
                        Buckets{" "}
                        {inheritedFromLast ? (
                            <span className="text-ink-faint italic">
                                (inherited from last <code className="font-mono">{s.mintAgent}</code> token — untick to override)
                            </span>
                        ) : (
                            <span className="text-ink-faint italic">
                                (no prior <code className="font-mono">{s.mintAgent}</code> — defaults to all)
                            </span>
                        )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                        <BucketCheckbox
                            bucketKey="*"
                            checked={effectiveCaps.includes("*")}
                            onToggle={() => dispatch({ type: "toggle_bucket", key: "*" })}
                            testid="mat-bucket-all"
                        />
                        {s.buckets.map((b) => (
                            <BucketCheckbox
                                key={b.key}
                                bucketKey={b.key}
                                count={b.count}
                                checked={effectiveCaps.includes(b.key) && !effectiveCaps.includes("*")}
                                onToggle={() => dispatch({ type: "toggle_bucket", key: b.key })}
                                testid={`mat-bucket-${b.key}`}
                            />
                        ))}
                    </div>
                </div>
                {s.mintErr ? (
                    <div className="mb-2 text-[11px] text-stamp-red font-body" data-testid="mat-mint-err">
                        {s.mintErr}
                    </div>
                ) : null}
                <button
                    type="button"
                    onClick={mint}
                    disabled={s.mintBusy}
                    data-testid="mat-mint-submit"
                    className="font-heading uppercase text-[10px] tracking-[0.22em] px-4 py-2 border-2 border-stamp-red text-stamp-red hover:bg-stamp-red/10 disabled:opacity-50 inline-flex items-center gap-1.5"
                >
                    {s.mintBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                    Mint {s.mintAgent} token
                </button>
            </div>

            {/* Token list */}
            <div className="divide-y divide-ink/15" data-testid="mat-token-list">
                {s.loading && s.tokens.length === 0 ? (
                    <div className="font-body text-[11px] text-ink-faint italic" data-testid="mat-loading">
                        Loading tokens…
                    </div>
                ) : s.tokens.length === 0 ? (
                    <div className="font-body text-[11px] text-ink-faint italic" data-testid="mat-empty">
                        No per-agent tokens minted yet. Use the form above to mint one for Gemini or Copilot.
                    </div>
                ) : (
                    s.tokens.map((t) => (
                        <TokenRow
                            key={t.id}
                            token={t}
                            onRevoke={revoke}
                            revokingId={s.revokingId}
                        />
                    ))
                )}
            </div>
        </div>
    );
}
